import animate from '@play-co/timestep-core/lib/animate';
import View from '@play-co/timestep-core/lib/ui/View';
import ButtonView from '@play-co/timestep-core/lib/ui/widget/ButtonView';
import bitmapFonts from 'src/lib/bitmapFonts';
import uiConfig from 'src/lib/ui/config';
import { createEmitter } from 'src/lib/Emitter';
import { animDuration } from 'src/lib/utils';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import ImageScaleView from '@play-co/timestep-core/lib/ui/ImageScaleView';
import ButtonScaleView from 'src/lib/ui/components/ButtonScaleView';
import PopupBasicTitle, { PopupBasicTitleType } from './PopupBasicTitle';
import { trackFirstButtonTap } from 'src/lib/analytics/firstButtonTap';
import { ImageScaleViewOpts } from '@play-co/timestep-core/lib/ui/ImageScaleView';
import StateObserver from 'src/StateObserver';
import { hideLoading, showLoading } from 'src/state/ui';
import { AssetGroup } from '@play-co/timestep-core';
import sounds from 'src/lib/sounds';

// TODO: Typing #init with this type causes every class inheriting from PopupBasic to raise type errors.
export type PopupBasicOpts = {
  title?: string;
  message?: string;
};

export default abstract class PopupBasic<
  CloseHandler extends Function = () => void,
> {
  protected root: View;

  protected bg: ButtonView;
  protected box: View | ImageScaleView;
  protected title: PopupBasicTitle;
  protected message: LangBitmapFontTextView;
  protected buttonClose: ButtonScaleView;
  protected overlay: View;

  constructor(
    protected opts: {
      superview: View;
      // The only reason to pass the PopupID around is as a shorthand for closing the popup.
      // We pass the shorthand directly instead. This increases type-safety around `CloseArgs`.
      close: CloseHandler;
      width?: number;
      height?: number;
      offsetY?: number;
      zIndex?: number;
      boxType?: 'normal' | 'small';
      boxOpts?: ImageScaleViewOpts;
      closeableWithBg?: boolean;
      darkerBg?: boolean;
      skipBlackBorder?: boolean;
      skipTitle?: boolean;
      skipMessage?: boolean;
      skipBackground?: boolean;
      titleBannerType?: PopupBasicTitleType;
      tracking: { feature: string; $subFeature: string };
      titleIcon?: ImageScaleViewOpts;
    },
  ) {
    this.root = new View({
      width: opts.superview.style.width,
      height: opts.superview.style.height,
      zIndex: opts.zIndex || 9998,
      infinite: true,
    });

    const bgType = opts.darkerBg
      ? uiConfig.popups.backgroundDarker
      : uiConfig.popups.background;

    this.bg = new ButtonView({
      ...bgType,
      superview: this.root,
      onClick: () => {
        if (opts.closeableWithBg) {
          trackFirstButtonTap({
            feature: this.opts.tracking.feature,
            $subFeature: `${this.opts.tracking.$subFeature}-basic-background`,
          });
          opts.close();
        }
      },
    });

    this.box = this.createBox(opts);

    if (opts.boxType === 'normal') {
      if (this.box.style.width < uiConfig.popups.minimumWidth) {
        throw Error('Popup below minimum width!');
      }

      if (this.box.style.height < uiConfig.popups.minimumHeight) {
        throw Error('Popup below minimum height!');
      }
    }

    this.overlay = new View({
      superview: this.root,
      infinite: true,
      zIndex: 10000,
      visible: false,
    });

    this.title = new PopupBasicTitle({
      superview: this.box,
      type: opts.titleBannerType,
      icon: opts.titleIcon,
    });

    if (opts.skipTitle) {
      this.title.getView().removeFromSuperview();
    }

    this.message = new LangBitmapFontTextView({
      superview: this.box,
      x: 40,
      y: 55,
      width: this.box.style.width - 80,
      align: 'center',
      verticalAlign: 'center',
      size: 26,
      color: '#BF8147',
      wordWrap: true,
      font: bitmapFonts('Body'),
      isRichText: true,
      canHandleEvents: false,
    });

    if (opts.skipMessage) {
      this.message.removeFromSuperview();
    }

    const closeButtonRightOffset = 10;
    const closeButtonOpts = uiConfig.buttons.close;
    const closeOpts = {
      ...closeButtonOpts,
      x:
        this.box.style.width +
        closeButtonRightOffset -
        closeButtonOpts.width * 0.5,
      y: 11,
    };

    this.buttonClose = new ButtonScaleView({
      superview: this.box,
      ...closeOpts,
      onClick: async () => {
        sounds.sfx.play('cancel');
        opts.close();
      },
      tracking: {
        feature: this.opts.tracking.feature,
        $subFeature: `${this.opts.tracking.$subFeature}-basic-close`,
      },
    });

    // anchor elements
    createEmitter(this.root, ({ ui }) => ui.screenSize).addListener(
      (screen) => {
        this.bg.updateOpts({
          width: screen.width,
          height: screen.height,
          opacity: 1,
        });
      },
    );
  }

  /**
   * Create a box with a background or without it.
   */
  protected createBox(opts: {
    skipBackground?: boolean;
    boxOpts?: ImageScaleViewOpts;
    width?: number;
    height?: number;
    offsetY?: number;
  }) {
    return opts.skipBackground
      ? new View({
          superview: this.root,
          infinite: true,
          canHandleEvents: false,
          width: opts.width || 591,
          height: opts.height || 903,
          x: this.root.style.width * 0.5,
          y: this.root.style.height * 0.5 + (opts.offsetY || 0),
          centerOnOrigin: true,
          centerAnchor: true,
        })
      : new ImageScaleView({
          ...(opts.boxOpts ? opts.boxOpts : uiConfig.popups.buruburu),
          superview: this.root,
          infinite: true,
          canHandleEvents: false,
          width: opts.width || 591,
          height: opts.height || 903,
          x: this.root.style.width * 0.5,
          y: this.root.style.height * 0.5 + (opts.offsetY || 0),
          centerOnOrigin: true,
          centerAnchor: true,
        });
  }

  public getBG() {
    return this.bg;
  }

  public getView(): View {
    return this.root;
  }

  public getBox(): View {
    return this.box;
  }

  public getButtonClose(): View {
    return this.buttonClose;
  }

  public init(opts: any) {
    const { title, message, titleIcon, banner } = opts;
    if (this.title) {
      this.title.setText(() => title || '');
      titleIcon && this.title.setIcon(titleIcon);
      banner && this.title.setBanner(banner);
    }
    if (this.message) this.message.localeText = () => message || '';

    this.fadeIn();
  }

  public async fadeIn() {
    this.overlay.show();
    this.attachRoot();
    this.box.hide();

    this.bg.updateOpts({ opacity: 0 });
    animate(this.bg)
      .clear()
      .then({ opacity: 1 }, animDuration, animate.easeOut);

    // We want to show bg first and then loading animation
    await this.loadAssets();

    this.box.show();
    this.box.updateOpts({ scale: 0 });
    animate(this.box)
      .clear()
      .wait(animDuration)
      .then({ scale: 1 }, animDuration, animate.easeOut)
      .then(() => this.overlay.hide());
  }

  public fadeOut() {
    this.overlay.show();

    animate(this.bg)
      .clear()
      .wait(animDuration)
      .then({ opacity: 0 }, animDuration, animate.easeOut)
      .then(() => {
        this.detachRoot();
        this.overlay.hide();
      });

    animate(this.box)
      .clear()
      .wait(0)
      .then({ scale: 0 }, animDuration, animate.easeOut);
  }

  //

  protected attachRoot() {
    this.opts.superview.addSubview(this.root);
  }

  protected detachRoot() {
    this.root.removeFromSuperview();
  }

  protected abstract getAssetsGroup(): AssetGroup | null;

  protected async loadAssets() {
    const assetsGroup = this.getAssetsGroup();
    if (!assetsGroup || assetsGroup.isLoaded()) return;

    StateObserver.dispatch(showLoading());
    try {
      await assetsGroup.load();
    } finally {
      StateObserver.dispatch(hideLoading());
    }
  }
}
